/**
 * RewardsSettingsForm - Edit rewards_config for a project
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Switch } from '../../ui/components/Switch';
import { Select } from '../../ui/components/Select';
import { useRewardsConfig, useUpdateRewardsConfig } from '../../hooks/useDistribution';

interface RewardsSettingsFormProps {
  projectSlug: string;
  projectCollections?: Array<{ id: string; name: string }>;
}

export const RewardsSettingsForm: React.FC<RewardsSettingsFormProps> = ({ projectSlug, projectCollections = [] }) => {
  const { data, isLoading } = useRewardsConfig(projectSlug);
  const updateMutation = useUpdateRewardsConfig(projectSlug);

  const [distributionMode, setDistributionMode] = useState<'all_holders' | 'claim_required'>('all_holders');
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [periodDuration, setPeriodDuration] = useState('');
  const [rewardAddressType, setRewardAddressType] = useState('coreum');
  const [rewardCurrencyLabel, setRewardCurrencyLabel] = useState('COREUM');
  const [poolSourceDefault, setPoolSourceDefault] = useState<string>('compound_entries');
  const [minPointsThreshold, setMinPointsThreshold] = useState('0');
  const [eligibleCollectionIds, setEligibleCollectionIds] = useState<string[]>([]);

  useEffect(() => {
    if (data?.config) {
      const c = data.config;
      setDistributionMode(c.distribution_mode || 'all_holders');
      setAutoAdvance(c.auto_advance ?? false);
      setPeriodDuration(c.period_duration_days != null ? String(c.period_duration_days) : '');
      setRewardAddressType(c.reward_address_type || 'coreum');
      setRewardCurrencyLabel(c.reward_currency_label || 'COREUM');
      setPoolSourceDefault(c.pool_source_default || 'compound_entries');
      setMinPointsThreshold(String(c.min_points_threshold ?? 0));
      setEligibleCollectionIds(c.eligible_collection_ids || []);
    }
  }, [data]);

  const toggleCollection = (id: string) => {
    setEligibleCollectionIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    updateMutation.mutate({
      distribution_mode: distributionMode,
      auto_advance: autoAdvance,
      period_duration_days: periodDuration ? parseInt(periodDuration) : null,
      reward_address_type: rewardAddressType,
      reward_currency_label: rewardCurrencyLabel,
      pool_source_default: poolSourceDefault as any,
      min_points_threshold: parseInt(minPointsThreshold) || 0,
      eligible_collection_ids: eligibleCollectionIds,
    });
  };

  if (isLoading) {
    return <div className="text-subtext-color py-4">Loading settings...</div>;
  }

  return (
    <div className="flex flex-col gap-4 max-w-lg">
      <span className="text-body-bold font-body-bold text-default-font">
        Distribution Settings
      </span>

      <div className="flex flex-col gap-2">
        <span className="text-caption-bold font-caption-bold text-default-font">Distribution Mode</span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={distributionMode === 'all_holders'}
              onChange={() => setDistributionMode('all_holders')}
            />
            <span className="text-body font-body text-default-font">All Holders</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={distributionMode === 'claim_required'}
              onChange={() => setDistributionMode('claim_required')}
            />
            <span className="text-body font-body text-default-font">Claim Required</span>
          </label>
        </div>
      </div>

      {projectCollections.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-caption-bold font-caption-bold text-default-font">Eligible Collections</span>
          <span className="text-caption font-caption text-subtext-color">
            Select which collections count toward reward points. At least one is required to start a period.
          </span>
          <div className="flex flex-col gap-1">
            {projectCollections.map(col => (
              <label key={col.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={eligibleCollectionIds.includes(col.id)}
                  onChange={() => toggleCollection(col.id)}
                />
                <span className="text-body font-body text-default-font">{col.name}</span>
              </label>
            ))}
          </div>
          {eligibleCollectionIds.length === 0 && (
            <span className="text-caption font-caption text-yellow-500">
              No collections selected. You must select at least one to start a distribution period.
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Switch checked={autoAdvance} onCheckedChange={setAutoAdvance} />
        <span className="text-body font-body text-default-font">
          Auto-advance to next period after distribution
        </span>
      </div>

      <TextField className="h-auto w-full flex-none" label="Period Duration (days)" helpText="Leave empty for manual periods">
        <TextField.Input
          type="number"
          min="1"
          placeholder="e.g., 30"
          value={periodDuration}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPeriodDuration(e.target.value)}
        />
      </TextField>

      <div className="flex flex-col gap-2">
        <span className="text-caption-bold font-caption-bold text-default-font">Reward Address Type</span>
        <div className="flex gap-4">
          {['coreum', 'xrp', 'other'].map(type => (
            <label key={type} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={rewardAddressType === type}
                onChange={() => setRewardAddressType(type)}
              />
              <span className="text-body font-body text-default-font capitalize">{type}</span>
            </label>
          ))}
        </div>
      </div>

      <TextField className="h-auto w-full flex-none" label="Reward Currency Label">
        <TextField.Input
          value={rewardCurrencyLabel}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRewardCurrencyLabel(e.target.value)}
        />
      </TextField>

      <div className="flex flex-col gap-2">
        <span className="text-caption-bold font-caption-bold text-default-font">Default Pool Source</span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={poolSourceDefault === 'compound_entries'}
              onChange={() => setPoolSourceDefault('compound_entries')}
            />
            <span className="text-body font-body text-default-font">Compound Entries</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={poolSourceDefault === 'manual_pool'}
              onChange={() => setPoolSourceDefault('manual_pool')}
            />
            <span className="text-body font-body text-default-font">Manual Pool</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={poolSourceDefault === 'manual_rate'}
              onChange={() => setPoolSourceDefault('manual_rate')}
            />
            <span className="text-body font-body text-default-font">Manual Rate</span>
          </label>
        </div>
      </div>

      <TextField className="h-auto w-full flex-none" label="Minimum Points Threshold">
        <TextField.Input
          type="number"
          min="0"
          value={minPointsThreshold}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinPointsThreshold(e.target.value)}
        />
      </TextField>

      {updateMutation.error && (
        <span className="text-caption font-caption text-red-600">{updateMutation.error.message}</span>
      )}

      {updateMutation.isSuccess && (
        <span className="text-caption font-caption text-green-600">Settings saved</span>
      )}

      <Button
        className="w-fit"
        variant="brand-primary"
        disabled={updateMutation.isPending}
        onClick={handleSave}
      >
        {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};
